import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';
import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
import { Link } from 'react-router-dom'; // تأكد من إضافة import

/** =========================
 * INNER REQUEST SHAPE
 * ========================= */
type InterBranchStatementRequestDto = {
  pageIndex?: number;
  pageSize?: number;
  fromDate?: string | null;
  toDate?: string | null;
  branchId?: string | null;
  counterId?: string | null;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  jE_HEADER_ID: string | null;
  gL_DATE: string | null;
  doC_ID: string | null;
  referencE_ID: string | null;
  code: string | null;
  name: string | null;
  namE2: string | null;
  branch: string | null;
  counterpartY_BRANCH: string | null;
  entereD_DR: number;
  entereD_CR: number;
  cumulativE_BALANCE: number;
};

const REPORT_KEY = 'RPT_InterBranchStatement';
const ENDPOINT = 'FinReports/GetInterBranchStatementPaged';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number, digits: number = 2) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(num || 0);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

// دالة لتحديد النوع بناءً على الكود
const getTransactionType = (code: string | null, isAr: boolean): string => {
  if (!code) return isAr ? '—' : '—';

  if (code.startsWith('CASH')) return isAr ? 'تحويل نقدي' : 'Cash Transfer';
  if (code.startsWith('INV')) return isAr ? 'فاتورة مدفوعات' : 'Invoice Payment';
  if (code.startsWith('MOR')) return isAr ? 'استلام تحويل مخزني' : 'Material Receipt';
  if (code.startsWith('MOI')) return isAr ? 'صرف تحويل مخزني' : 'Material Issue';

  return isAr ? 'أخرى' : 'Other';
};

// دالة لإنشاء الرابط المناسب
const getTransactionLink = (code: string | null, referenceId: string | null): string => {
  if (!code || !referenceId) return '#';
  if (code.startsWith('CASH')) return `/finance/cash-transfer/${referenceId}`;
  if (code.startsWith('INV')) return `/AP/ApInvoices/${referenceId}/UpdateApInvoices`;
  if (code.startsWith('MOR')) return `/INV/Transactions/InvReceive/${referenceId}/UpdateInvReceive`;
  if (code.startsWith('MOI')) return `/INV/Transactions/InvIssue/${referenceId}/UpdateInvIssue`;
  //   debugger

  return '#';
};

const InterBranchStatement_Paged = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // options
  const { OptionsBranches, loading: loading2 } = useGlChartLookups();

  // Filters state
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [counterFilter, setCounterFilter] = useState<any>(null);

  // Date range
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;
  const isAr = i18n.language !== 'en';

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'تاريخ القيد', headerEn: 'GL Date', value: r => formatDate(r.gL_DATE) },
    { headerAr: 'النوع', headerEn: 'Type', value: r => getTransactionType(r.code, isAr) },
    { headerAr: 'الرمز', headerEn: 'Code', value: r => r.code || '' },
    { headerAr: 'الفرع', headerEn: 'Branch', value: r => r.branch || '' },
    { headerAr: 'الفرع المقابل', headerEn: 'Counter Branch', value: r => r.counterpartY_BRANCH || '' },
    { headerAr: 'مدين', headerEn: 'Debit', value: r => r.entereD_DR || 0 },
    { headerAr: 'دائن', headerEn: 'Credit', value: r => r.entereD_CR || 0 },
    { headerAr: 'الرصيد التراكمي', headerEn: 'Cumulative Balance', value: r => r.cumulativE_BALANCE || 0 },
    { headerAr: 'الوصف', headerEn: 'Description', value: r => (isAr ? r.namE2 || r.name : r.name || r.namE2) || '' },
    { headerAr: 'معرف المستند', headerEn: 'Doc ID', value: r => r.doC_ID || '' }
  ];

  useEffect(() => {
    const initialRequest: InterBranchStatementRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );
  }, [dispatch]);

  const applyFilters = () => {
    const branchId = getOptId(branchFilter);
    const counterId = getOptId(counterFilter);

    if (!branchId || !counterId) return;

    const prevReq = (((report as any)?.request ?? {}) as InterBranchStatementRequestDto) || {};

    const nextReq: InterBranchStatementRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      fromDate: isoOrNull(fromDate),
      toDate: isoOrNull(toDate),
      branchId: branchId,
      counterId: counterId
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setBranchFilter(null);
    setCounterFilter(null);
    setFromDate(null);
    setToDate(null);

    const resetReq: InterBranchStatementRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
  };

  // Totals Cards Component
  //   const TotalsCards = () => {
  //     if (!meta || !branchFilter || !counterFilter) return null;

  //     return (
  //       <div className='row g-3 mb-4'>
  //         {/* رصيد افتتاحي */}
  //         <div className='col-lg-3 col-md-6'>
  //           <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
  //             <div className='card-body d-flex align-items-center'>
  //               <div className='  p-3 me-3'>
  //                 <i className='fas fa-scale-balanced text-primary fa-xl'></i>
  //               </div>
  //               <div>
  //                 <h6 className='text-primary fw-bold mb-1'>{isAr ? 'الرصيد الافتتاحي' : 'Opening Balance'}</h6>
  //                 <h4 className='mb-0 fw-bold'>{formatNumber(meta.openingBalance)}</h4>
  //               </div>
  //             </div>
  //           </Card>
  //         </div>

  //         {/* مدين الفترة */}
  //         <div className='col-lg-3 col-md-6'>
  //           <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
  //             <div>
  //               <div className='card-body d-flex align-items-center'>
  //                 <div className='  p-3 me-3'>
  //                   <i className='fas fa-arrow-up text-success fa-xl'></i>
  //                 </div>
  //                 <div>
  //                   <h6 className='text-success fw-bold mb-1'>{isAr ? 'إجمالي مدين' : 'Total Debit'}</h6>
  //                   <h4 className='mb-0 fw-bold'>{formatNumber(meta.totalPeriodDr)}</h4>
  //                 </div>
  //               </div>
  //             </div>
  //           </Card>
  //         </div>

  //         {/* دائن الفترة */}
  //         <div className='col-lg-3 col-md-6'>
  //           <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
  //             <div>
  //               <div className='card-body d-flex align-items-center'>
  //                 <div className=' p-3 me-3'>
  //                   <i className='fas fa-arrow-down text-danger fa-xl'></i>
  //                 </div>
  //                 <div>
  //                   <h6 className='text-danger fw-bold mb-1'>{isAr ? 'إجمالي دائن' : 'Total Credit'}</h6>
  //                   <h4 className='mb-0 fw-bold'>{formatNumber(meta.totalPeriodCr)}</h4>
  //                 </div>
  //               </div>
  //             </div>
  //           </Card>
  //         </div>

  //         {/* الرصيد النهائي */}
  //         <div className='col-lg-3 col-md-6'>
  //           <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
  //             <div>
  //               <div className='card-body d-flex align-items-center'>
  //                 <div className=' p-3 me-3'>
  //                   <i className='fas fa-chart-line text-info fa-xl'></i>
  //                 </div>
  //                 <div>
  //                   <h6 className='text-info fw-bold mb-1'>{isAr ? 'الرصيد الختامي' : 'Closing Balance'}</h6>
  //                   <h4 className='mb-0 fw-bold'>{formatNumber(meta.finalClosingBalance)}</h4>
  //                 </div>
  //               </div>
  //             </div>
  //           </Card>
  //         </div>
  //       </div>
  //     );
  //   };

  // Totals Cards Component
  const TotalsCards = () => {
    // لا نعرض البطاقات إلا إذا كان هناك بيانات وصفية وتم اختيار الفروع
    if (!meta || !branchFilter || !counterFilter) return null;

    const balance = meta.finalClosingBalance || 0;
    const branchName = isAr ? branchFilter.label : branchFilter.label;
    const counterName = isAr ? counterFilter.label : counterFilter.label;

    /**
     * دالة لتحديد نص حالة المديونية:
     * - إذا كان الرصيد (موجب): الفرع الرئيسي يطلب الفرع المقابل (مدين له)
     * - إذا كان الرصيد (سالب): الفرع المقابل يطلب الفرع الرئيسي (دائن له)
     */
    const getDebtStatus = () => {
      if (balance === 0) return isAr ? 'الحساب متزن تماماً' : 'Account is perfectly balanced';

      // if (balance > 0) {
      if (balance < 0) {
        return isAr
          ? ` [${branchName}] يطلب  [${counterName}] بمبلغ ${formatNumber(balance)}`
          : ` [${branchName}] is owed ${formatNumber(balance)} by [${counterName}]`;
      } else {
        // نستخدم Math.abs لتحويل الرقم لـ موجب في النص التوضيحي
        return isAr
          ? ` [${counterName}] يطلب  [${branchName}] بمبلغ ${formatNumber(Math.abs(balance))}`
          : ` [${counterName}] is owed ${formatNumber(Math.abs(balance))} by [${branchName}]`;
      }
    };


    


    return (
      <div className='row g-3 mb-4'>
        {/* 1. الرصيد الافتتاحي */}
        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-primary bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='  p-3 rounded-circle me-3'>
                <i className='fas fa-scale-balanced text-primary fa-xl'></i>
              </div>
              <div>
                <h6 className='text-primary fw-bold mb-1'>{isAr ? 'الرصيد الافتتاحي' : 'Opening Balance'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(meta.openingBalance)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* 2. إجمالي مدين (الحركات التي زادت المديونية) */}
        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-danger bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='  p-3 rounded-circle me-3'>
                <i className='fas fa-arrow-down text-danger fa-xl'></i>
              </div>
              <div>
                <h6 className='text-danger fw-bold mb-1'>{isAr ? 'إجمالي مدين' : 'Total Debit'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(meta.totalPeriodDr)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* 3. إجمالي دائن (الحركات التي خفضت المديونية) */}
        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-success bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body d-flex align-items-center'>
              <div className='  p-3 rounded-circle me-3'>
                <i className='fas fa-arrow-up text-success fa-xl'></i>
              </div>
              <div>
                <h6 className='text-success fw-bold mb-1'>{isAr ? 'إجمالي دائن' : 'Total Credit'}</h6>
                <h4 className='mb-0 fw-bold'>{formatNumber(meta.totalPeriodCr)}</h4>
              </div>
            </div>
          </Card>
        </div>

        {/* 4. الرصيد الختامي مع البيان الذكي */}
        <div className='col-lg-3 col-md-6'>
          <Card className='card bg-info bg-opacity-10 border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='d-flex align-items-center mb-2'>
                <div className='  p-3 rounded-circle me-3'>
                  <i className='fas fa-chart-line text-info fa-xl'></i>
                </div>
                <div>
                  <h6 className='text-info fw-bold mb-1'>{isAr ? 'الرصيد الختامي' : 'Closing Balance'}</h6>
                  <h4 className={`mb-0 fw-bold ${balance < 0 ? 'text-danger' : 'text-success'}`}>
                    {formatNumber(balance)}
                  </h4>
                </div>
              </div>

              {/* سطر البيان التوضيحي ذو الخلفية الملونة */}
              <div
                className={`mt-2 p-2 rounded small fw-bold d-flex align-items-center ${
                  balance < 0
                    ? 'bg-danger bg-opacity-25 text-danger'
                    : balance > 0
                      ? 'bg-success bg-opacity-25 text-success'
                      : 'bg-secondary bg-opacity-25 text-muted'
                }`}
              >
                <i
                  className={`fas ${balance < 0 ? 'fa-hand-holding-dollar' : balance > 0 ? 'fa-money-bill-trend-up' : 'fa-check-circle'} me-2`}
                ></i>
                <span>{getDebtStatus()}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center'>
          {/* الفرع الرئيسي (إلزامي) */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-store text-primary'></i>
                <span className='text-danger ms-1'>*</span>
              </span>
              <Select
                isLoading={loading2}
                value={branchFilter}
                onChange={setBranchFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع' : 'Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* الفرع المقابل (إلزامي) */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-exchange-alt text-warning'></i>
                <span className='text-danger ms-1'>*</span>
              </span>
              <Select
                isLoading={loading2}
                value={counterFilter}
                onChange={setCounterFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع المقابل' : 'Counter Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* من تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* إلى تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className='col-lg-2 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
              disabled={!branchFilter && !counterFilter && !fromDate && !toDate}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button
              className='btn btn-theme me-2'
              type='button'
              onClick={applyFilters}
              title={t('ApplyFilters')}
              disabled={!branchFilter || !counterFilter}
            >
              <i className='fas fa-filter fa-fw'></i>
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || items.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>
      </CardHeader>

      {/* كروت الإجماليات */}
      <Loading loading={loading} error={error}>
        <TotalsCards />
        <Card className='mb-4'>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover mb-0'>
              <thead className='sticky-top' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center'>{t('Date')}</div>
                  </th>
                  <th className='small' style={{ minWidth: '100px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'النوع' : 'Type'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'الفرع' : 'Branch'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'الفرع المقابل' : 'Counter Branch'}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Debit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Credit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'الرصيد' : 'Balance'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '350px' }}>
                    <div className='d-flex align-items-center'>{t('Description')}</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const desc = isAr ? r.namE2 || r.name : r.name || r.namE2;
                    const isOpening = !r.jE_HEADER_ID && !r.gL_DATE;
                    const transactionType = getTransactionType(r.code, isAr);
                    const linkTo = getTransactionLink(r.code, r.referencE_ID);
                    const hasValidLink = linkTo !== '#' && r.code && r.referencE_ID;

                    console.log(linkTo);

                    return (
                      <tr
                        key={`${r.jE_HEADER_ID || 'OB'}-${idx}`}
                        className={`align-middle ${isOpening ? 'table-light fw-bold' : ''}`}
                      >
                        <td>
                          <div className={isOpening ? 'text-primary' : 'fw-medium'}>
                            {r.gL_DATE ? r.gL_DATE.split('T')[0] : isAr ? 'رصيد افتتاحي' : 'Opening'}
                          </div>
                        </td>

                        <td>
                          <span
                            className={`badge ${
                              r.code?.startsWith('CASH')
                                ? 'bg-info'
                                : r.code?.startsWith('INV')
                                  ? 'bg-warning'
                                  : r.code?.startsWith('MOR')
                                    ? 'bg-success'
                                    : r.code?.startsWith('MOI')
                                      ? 'bg-danger'
                                      : 'bg-secondary'
                            }`}
                          >
                            {transactionType}
                          </span>
                          {hasValidLink ? (
                            <Link
                              to={linkTo}
                              className='text-decoration-none fw-medium'
                              //   target='_blank'
                              //   rel='noopener noreferrer'
                            >
                              {safe(r.code)}
                              <i className='fas fa-external-link-alt ms-1 small'></i>
                            </Link>
                          ) : (
                            <span className='text-muted'>{safe(r.code)}</span>
                          )}
                        </td>

                        <td>
                          <div className='fw-medium'>{safe(r.branch)}</div>
                        </td>

                        <td>
                          <div className='fw-medium'>{safe(r.counterpartY_BRANCH)}</div>
                        </td>

                        {/* <td className='text-end'>
                          {r.entereD_DR > 0 ? (
                            <div className='fw-bold text-success'>{formatNumber(r.entereD_DR)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td> */}

                        <td className='text-end'>
                          {r.entereD_DR > 0 ? (
                            <div className='fw-bold text-dark'>{formatNumber(r.entereD_DR)}</div>
                          ) : (
                            <div className='text-muted opacity-50'>0.00</div>
                          )}
                        </td>

                        {/* <td className='text-end'>
                          {r.entereD_CR > 0 ? (
                            <div className='fw-bold text-danger'>{formatNumber(r.entereD_CR)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td> */}

                        <td className='text-end'>
                          {r.entereD_CR > 0 ? (
                            <div className='fw-bold text-dark'>{formatNumber(r.entereD_CR)}</div>
                          ) : (
                            <div className='text-muted opacity-50'>0.00</div>
                          )}
                        </td>

                        <td className='text-center bg-secondary bg-opacity-30'>
                          <div className={`fw-bold fs-5  ${r.cumulativE_BALANCE < 0 ? 'text-danger' : 'text-success'}`}>
                            {formatNumber(r.cumulativE_BALANCE)}
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className={desc ? '' : 'text-muted fst-italic'}>
                              {safe(desc, isAr ? 'لا يوجد وصف' : 'No description')}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : !loading && (!branchFilter || !counterFilter) ? (
                  <tr>
                    <td colSpan={9} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-code-branch fa-3x mb-3 opacity-50 text-warning'></i>
                        <h6 className='text-warning'>
                          {isAr
                            ? 'الرجاء اختيار الفرع والفرع المقابل أولاً'
                            : 'Please select Branch and Counter Branch first'}
                        </h6>
                      </div>
                    </td>
                  </tr>
                ) : !loading ? (
                  <tr>
                    <td colSpan={9} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-file-invoice fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                        <small>{t('TryChangingFilters')}</small>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <CardFooter>
            <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
              <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />

              {!loading && items.length > 0 && (
                <div className='text-muted small'>
                  <i className='fas fa-info-circle me-1'></i>
                  {isAr ? 'عرض' : 'Showing'} {items.length} {isAr ? 'من' : 'of'} {meta?.totalRows || 0}{' '}
                  {isAr ? 'سجل' : 'records'}
                </div>
              )}
            </div>
          </CardFooter>
        </Card>
      </Loading>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'كشف حساب بين الفروع' : 'Inter-Branch Statement')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default InterBranchStatement_Paged;
